import {
  Alert,
  AlertActionCloseButton,
  AlertGroup,
} from "@patternfly/react-core";
import React, {
  PropsWithChildren,
  useCallback,
  useMemo,
  useState,
  useTransition,
} from "react";
import ToastContext, { Toast } from "../../shared/context/toast-context";
import { useTranslation } from "react-i18next";

type ToastWithKey = {
  key: React.Key;
} & Toast;

type ToastAlertProviderProps = PropsWithChildren;

export default ToastAlertProvider;

function ToastAlertProvider({ children }: Readonly<ToastAlertProviderProps>) {
  const [toasts, setToasts] = useState<ToastWithKey[]>([]);
  const [, startTransition] = useTransition();
  const [key, setKey] = useState<number>(0);
  const removeToast = useCallback(
    (key: React.Key) => () => {
      setToasts((toasts) => [...toasts.filter((toast) => toast.key !== key)]);
    },
    [],
  );
  const dispatch = useCallback(
    (toast: Toast) => {
      const MAX_TOASTS = 10;
      startTransition(() => {
        setToasts((toasts) => [
          ...toasts.slice(-(MAX_TOASTS - 1)),
          { ...toast, key },
        ]);
      });
      if (toast.timeout === undefined || toast.timeout === true) {
        // https://www.patternfly.org/v4/components/alert
        const DEFAULT_PATTERNFLY_TOAST_TIMEOUT = 8000;
        setTimeout(removeToast(key), DEFAULT_PATTERNFLY_TOAST_TIMEOUT);
      } else if (toast.timeout !== false) {
        setTimeout(removeToast(key), toast.timeout);
      }
      setKey((key) => key + 1);
    },
    [key, removeToast],
  );
  const alerts = useMemo(() => {
    return toasts.map(
      ({ key, timeout, content, variant, dismissable = true }) => (
        <AlertWithTitle
          key={key}
          timeout={timeout}
          variant={variant}
          dismissable={dismissable}
          onClose={removeToast(key)}
        >
          {content}
        </AlertWithTitle>
      ),
    );
  }, [toasts, removeToast]);
  return (
    <ToastContext.Provider value={dispatch}>
      <AlertGroup isToast isLiveRegion aria-live="assertive">
        {alerts}
      </AlertGroup>
      {children}
    </ToastContext.Provider>
  );
}

type AlertWithTitleProps = {
  onClose?: () => void;
} & Pick<Toast, "variant" | "timeout" | "dismissable"> &
  PropsWithChildren;

function AlertWithTitle({
  variant,
  timeout,
  dismissable,
  children,
  onClose,
}: AlertWithTitleProps) {
  const { t: translateToastTitle } = useTranslation("toast-titles");
  const title = useMemo(
    () => translateToastTitle(variant ?? "info"),
    [variant, translateToastTitle],
  );
  return (
    <Alert
      variant={variant}
      title={title}
      timeout={timeout}
      role="alert"
      actionClose={
        dismissable === true && (
          <AlertActionCloseButton
            title={title}
            variantLabel={`${variant?.toString() ?? "default"} alert`}
            onClose={onClose}
          />
        )
      }
    >
      {children}
    </Alert>
  );
}
